import { AppCard, AppPage } from '../../components/general';
import { BreadcrumbItem } from '../../components/general/AppPage';
import { FC, useState } from 'react';
import { Button } from 'primereact/button';
import { DataTableSearchCreateHeader } from '../../components/datatable';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';
import { WaterfallViewModel } from '../../models/waterfall';
import { toLocalDateTime } from '../../utils/date-helper';
import { Tag } from 'primereact/tag';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';

const TABLE_ROW_COUNT = 10;

interface WaterfallsProps {
  waterfalls: WaterfallViewModel[];
  onDelete: (id: WaterfallViewModel['waterfallId']) => void;
  integrationType?: string;
  title?: string;
  basePath?: string;
  breadcrumbs?: BreadcrumbItem[];
}

const defaultBreadcrumbs = (basePath: string, title: string): BreadcrumbItem[] => {
  const map: Record<string, { label: string; to: string }> = {
    '/credit/waterfalls': { label: 'Credit', to: '/products/credit' },
    '/fees/waterfalls': { label: 'Fees', to: '/products/fees' },
    '/vox/waterfalls': { label: 'VoX', to: '/products/vox' },
    '/auw/waterfalls': { label: 'AUW', to: '/products/auw' },
  };
  const parent = map[basePath];
  if (parent) return [{ label: 'Dashboard', to: '/dashboard' }, parent, { label: 'Waterfalls' }];
  return [{ label: 'Dashboard', to: '/dashboard' }, { label: title }];
};

export const Waterfalls: FC<WaterfallsProps> = ({ waterfalls, onDelete, integrationType, title = 'Waterfalls', basePath = '/waterfalls', breadcrumbs }) => {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');

  const typeFiltered = integrationType
    ? waterfalls.filter((w) => w.integrationType === integrationType)
    : waterfalls;

  const filtered = typeFiltered.filter(
    (w) =>
      w.name?.toLowerCase().includes(search.toLowerCase()) || w.integrationType?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppPage title={title} breadcrumbs={breadcrumbs || defaultBreadcrumbs(basePath, title)}>
      <AppCard>
        <div className="flex justify-between mb-4">
          <div className="flex gap-2 items-center">
            <DataTableSearchCreateHeader createNewDisabled onSearchChange={(value: string) => setSearch(value)} />
          </div>

          <Button
            label="+ Add Waterfall"
            className="p-button-sm p-button-primary"
            onClick={() => navigate(`${basePath}/new`)}
          />
        </div>

        <DataTable
          value={filtered}
          paginator
          rows={TABLE_ROW_COUNT}
          stripedRows
          emptyMessage="No waterfalls found"
          className="p-datatable-sm"
        >
          <Column field="name" header="Name" sortable />
          <Column field="integrationType" header="Type" sortable />
          <Column
            field="lastExecuted"
            header="Last Executed"
            body={(row) => (row.lastExecuted ? toLocalDateTime(row.lastExecuted) : 'Never')}
            sortable
          />
          <Column
            header="Active"
            body={(row) => (
              <Tag value={row.disabled ? 'Disabled' : 'Active'} severity={row.disabled ? 'secondary' : 'success'} />
            )}
          />
          <Column
            header="Actions"
            style={{ width: '8rem' }}
            body={(row) => (
              <div className="flex gap-1">
                <Button
                  icon="pi pi-pencil"
                  className="p-button-text p-button-sm"
                  onClick={() => navigate(`${basePath}/edit/${row.waterfallId}`)}
                  tooltip="Edit"
                  tooltipOptions={{
                    position: 'top',
                  }}
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-text p-button-sm p-button-danger"
                  onClick={(e) => {
                    confirmPopup({
                      target: e.currentTarget,
                      message: 'Are you sure you want to delete this waterfall?',
                      icon: 'pi pi-exclamation-triangle',
                      acceptClassName: 'p-button-danger p-button-sm',
                      accept: () => onDelete(row.waterfallId),
                      reject: () => {},
                    });
                  }}
                  tooltip="Delete"
                  tooltipOptions={{
                    position: 'top',
                  }}
                />
              </div>
            )}
          />
        </DataTable>
      </AppCard>
      <ConfirmPopup />
    </AppPage>
  );
};
